import { fabric } from 'fabric';

enum WordOrGraphemeTypeEnum {
  'NORMAL_WORD' = 'NORMAL_WORD',
  'FIRST_GRAPHEME_OF_LONG_WORD' = 'FIRST_GRAPHEME_OF_LONG_WORD',
  'LAST_GRAPHEME_OF_LONG_WORD' = 'LAST_GRAPHEME_OF_LONG_WORD',
  'GRAPHEME_OF_LONG_WORD' = 'GRAPHEME_OF_LONG_WORD',
}

interface WordOrGrapheme {
  value: string;
  type: WordOrGraphemeTypeEnum;
}

/**
 * Enable wrapping of words with width greater than the textbox maxWidth to avoid expanding the textbox
 */
export function enableLongWordWrapping(): void {
  /**
   * Override of fabric.Textbox.prototype._wrapText
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/shapes/textbox.class.ts#L246
   *
   * changes: updates addNewLineOffset array with the new values after wrapping each line
   */
  (fabric.Textbox.prototype as any)._wrapText = function (
    lines: Array<any>,
    desiredWidth: number,
  ): Array<any> {
    this.addNewLineOffset = [];
    let wrapped: any[] = [];
    let i;
    this.isWrapping = true;
    for (i = 0; i < lines.length; i++) {
      const { graphemeLines, missingNewLineOffset } = this._wrapLine(lines[i], i, desiredWidth);
      wrapped = wrapped.concat(graphemeLines);
      this.addNewLineOffset = this.addNewLineOffset.concat(missingNewLineOffset);
    }
    this.isWrapping = false;
    return wrapped;
  };

  /**
   * Override of fabric.Textbox.prototype._wrapLine
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/shapes/textbox.class.ts#L305
   *
   * changes:
   * - Added prepareLineForWrapping:
   *  - split long words (words with width greater than the textbox width) into graphemes (characters)
   *  - store the width of each word and grapheme so we don't have to measure them again
   *  - build wordOrGraphemeType array to store the type
   *
   * - Build missingNewLineOffset array according to the type of the last word or grapheme in the line,
   *   to be used in missingNewlineOffset function
   * - We changed the return type to be an object with graphemeLines and missingNewLineOffset
   */
  (fabric.Textbox.prototype as any)._wrapLine = function (
    _line: any,
    lineIndex: number,
    desiredWidth: number,
    reservedSpace = 0,
  ) {
    type GraphemeLine = (string | string[])[];

    let lineWidth = 0;
    const splitByGrapheme = this.splitByGrapheme;
    const graphemeLines: GraphemeLine[] = [];
    let line: GraphemeLine = [];
    // spaces in different languages?
    const words = splitByGrapheme
      ? fabric.util.string.graphemeSplit(_line)
      : _line.split(this._wordJoiners);
    let word = '';
    let offset = 0;
    const infix = splitByGrapheme ? '' : ' ';
    let wordWidth = 0;
    let infixWidth = 0;
    let largestWordWidth = 0;
    let lineJustStarted = true;
    const additionalSpace = this._getWidthOfCharSpacing();
    const missingNewLineOffset: boolean[] = [];
    reservedSpace = reservedSpace || 0;

    // fix a difference between split and graphemeSplit
    if (words.length === 0) {
      words.push([]);
    }
    desiredWidth -= reservedSpace;

    const { wordsOrGraphemes, wordsWidths } = prepareLineForWrapping.bind(this)(
      words,
      desiredWidth,
      lineIndex,
    );

    let i;
    for (i = 0; i < wordsOrGraphemes.length; i++) {
      // if using splitByGrapheme words are already in graphemes.
      word = splitByGrapheme
        ? words[i]
        : fabric.util.string.graphemeSplit(wordsOrGraphemes[i].value);
      wordWidth = wordsWidths[i];
      offset += word.length;

      lineWidth += infixWidth + wordWidth - additionalSpace;
      if (lineWidth > desiredWidth && !lineJustStarted) {
        graphemeLines.push(line);
        updateMissingNewLineOffset(i, wordsOrGraphemes, missingNewLineOffset);
        line = [];
        lineWidth = wordWidth;
        lineJustStarted = true;
      } else {
        lineWidth += additionalSpace;
      }

      infixWidth = 0;

      if (shouldAddInfix(wordsOrGraphemes, lineJustStarted, splitByGrapheme, i)) {
        line.push(infix);
      }

      if (shouldCalculateInfixWidth(wordsOrGraphemes, i)) {
        infixWidth = this._measureWord([infix], lineIndex, offset).width;
        offset++;
      }

      line = line.concat(word);

      lineJustStarted = false;
      // keep track of largest word
      if (wordWidth > largestWordWidth) {
        largestWordWidth = wordWidth;
      }
    }

    i && graphemeLines.push(line);
    missingNewLineOffset.push(true);

    if (largestWordWidth + reservedSpace > this.dynamicMinWidth) {
      this.dynamicMinWidth = largestWordWidth - additionalSpace + reservedSpace;
    }
    return { graphemeLines, missingNewLineOffset };
  };

  /**
   * Override of fabric.Textbox.prototype.missingNewlineOffset
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/shapes/textbox.class.ts#L407
   *
   * changes:
   * - using our own data structure (addNewLineOffset) to decide if there is a missing new line
   * - fix from fabric for issue #9197
   */
  (fabric.Textbox.prototype as any).missingNewlineOffset = function (
    lineIndex: number,
    skipWrapping?: boolean,
  ) {
    if (this.splitByGrapheme && !skipWrapping) {
      return this.isEndOfWrapping(lineIndex) ? 1 : 0;
    }
    if (skipWrapping || !this.addNewLineOffset || this.addNewLineOffset.length === 0) {
      return 1;
    }
    return this.addNewLineOffset[lineIndex] ? 1 : 0;
  };

  /**
   * Override of fabric.Textbox.prototype._measureWord
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/shapes/textbox.class.ts#L268
   *
   * changes: We changed the return value to be an object with width and graphemesWidth
   *  - width: the width of the word
   *  - graphemesWidth: an array of widths for each grapheme, to be used in _wrapLine instead of caluculating it again
   */
  (fabric.Textbox.prototype as any)._measureWord = function (
    word: any,
    lineIndex: number,
    charOffset = 0,
  ): { width: number; graphemesWidth: any[] } {
    const graphemesWidth: any[] = [];
    let width = 0;
    let prevGrapheme;
    const skipLeft = true;
    for (let i = 0, len = word.length; i < len; i++) {
      const box = this._getGraphemeBox(word[i], lineIndex, i + charOffset, prevGrapheme, skipLeft);
      graphemesWidth.push(box.kernedWidth);
      width += box.kernedWidth;
      prevGrapheme = word[i];
    }
    return { width, graphemesWidth };
  };

  /**
   * Override of fabric.Textbox.prototype._setTextStyles
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/shapes/text.class.ts#L606
   *
   * changes: set textBaseline to 'alphabetic' instead of 'alphabetical' to be accepted by HTML5 Canvas API
   * It's a typo in fabric code that was fixed in newer versions
   * Update the object caching flag for the textbox if needed
   */
  (fabric.Textbox.prototype as any)._setTextStyles = function (
    ctx: CanvasRenderingContext2D,
    charStyle: any,
    forMeasuring?: boolean,
  ) {
    ctx.textBaseline = 'alphabetic'; // changed line

    if (this.path) {
      switch (this.pathAlign) {
        case 'center':
          ctx.textBaseline = 'middle';
          break;
        case 'ascender':
          ctx.textBaseline = 'top';
          break;
        case 'descender':
          ctx.textBaseline = 'bottom';
          break;
      }
    }
    updateTextboxCachingFlag(this);

    ctx.font = this._getFontDeclaration(charStyle, forMeasuring);
  };

  /**
   * Override of fabric.Textbox.prototype.get2DCursorLocation
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/shapes/text.class.ts#L511
   *
   * changes: passing skipWrapping to missingNewLineOffset (fix from fabric for issue #9197)
   */
  (fabric.Textbox.prototype as any).get2DCursorLocation = function (
    selectionStart: number,
    skipWrapping?: boolean,
  ) {
    if (typeof selectionStart === 'undefined') {
      selectionStart = this.selectionStart;
    }
    const lines = skipWrapping ? this._unwrappedTextLines : this._textLines;
    let i: number;
    for (i = 0; i < lines.length; i++) {
      if (selectionStart <= lines[i].length) {
        return {
          lineIndex: i,
          charIndex: selectionStart,
        };
      }
      selectionStart -= lines[i].length + this.missingNewlineOffset(i, skipWrapping);
    }
    return {
      lineIndex: i - 1,
      charIndex: lines[i - 1].length < selectionStart ? lines[i - 1].length : selectionStart,
    };
  };
}

/**
 * Added function to prepare the line for wrapping by initializing wordsOrGraphemes and wordsWidths arrays
 * It measures the width of each word and grapheme and stores the width in the wordsWidths array
 * It also builds the wordsOrGraphemes array with the type of each word or grapheme
 */
const prepareLineForWrapping = function (
  this: any,
  words: string[],
  desiredWidth: number,
  lineIndex: number,
): { wordsOrGraphemes: WordOrGrapheme[]; wordsWidths: number[] } {
  const wordsOrGraphemes: WordOrGrapheme[] = [];
  const wordsWidths: number[] = [];
  let offset = 0;
  for (let i = 0; i < words.length; i++) {
    const measuredWord = this._measureWord(words[i], lineIndex, offset);
    const wordWidth = measuredWord.width;
    if (wordWidth > desiredWidth) {
      fabric.util.string.graphemeSplit(words[i]).forEach((grapheme, index) => {
        const wordOrGraphemeType =
          index === words[i].length - 1
            ? WordOrGraphemeTypeEnum.LAST_GRAPHEME_OF_LONG_WORD
            : index === 0
            ? WordOrGraphemeTypeEnum.FIRST_GRAPHEME_OF_LONG_WORD
            : WordOrGraphemeTypeEnum.GRAPHEME_OF_LONG_WORD;
        wordsOrGraphemes.push({
          value: grapheme,
          type: wordOrGraphemeType,
        });
        wordsWidths.push(measuredWord.graphemesWidth[index]);
      });
    } else {
      wordsOrGraphemes.push({
        value: words[i],
        type: WordOrGraphemeTypeEnum.NORMAL_WORD,
      });
      wordsWidths.push(wordWidth);
    }
    offset += words[i].length + 1;
  }
  return { wordsOrGraphemes, wordsWidths };
};

// if the previous is normal word or last grapheme of a long word, there is a missing new line
// if the previous is a grapheme of long word but not the last, there is no missing new line
const updateMissingNewLineOffset = function (
  index: number,
  wordsOrGraphemes: WordOrGrapheme[],
  missingNewLineOffset: boolean[],
) {
  if (
    wordsOrGraphemes[index - 1].type === WordOrGraphemeTypeEnum.FIRST_GRAPHEME_OF_LONG_WORD ||
    wordsOrGraphemes[index - 1].type === WordOrGraphemeTypeEnum.GRAPHEME_OF_LONG_WORD
  ) {
    missingNewLineOffset.push(false);
  } else {
    missingNewLineOffset.push(true);
  }
};

// in case of lineJustStarted = true, if the current is normal word or last grapheme of long word,
// we are sure that the next iteration will push an infix(space) to the line,
// so we need to count the width of this infix to be added to the lineWidth before adding the next word or character
// to make sure the comparison with desiredWidth is correct for the next iteration.
const shouldCalculateInfixWidth = function (
  wordsOrGraphemes: WordOrGrapheme[],
  index: number,
): boolean {
  return (
    wordsOrGraphemes[index].type === WordOrGraphemeTypeEnum.NORMAL_WORD ||
    wordsOrGraphemes[index].type === WordOrGraphemeTypeEnum.LAST_GRAPHEME_OF_LONG_WORD
  );
};

// add space if not the start of line, and the next is a normal word (not long) or the first grapheme of a long word
const shouldAddInfix = function (
  wordsOrGraphemes: WordOrGrapheme[],
  lineJustStarted: boolean,
  splitByGrapheme: boolean,
  index: number,
): boolean {
  return (
    !lineJustStarted &&
    !splitByGrapheme &&
    (wordsOrGraphemes[index].type === WordOrGraphemeTypeEnum.NORMAL_WORD ||
      wordsOrGraphemes[index].type === WordOrGraphemeTypeEnum.FIRST_GRAPHEME_OF_LONG_WORD)
  );
};

// turn off object caching for textboxes that are too large to keep a sharp resolution
const updateTextboxCachingFlag = function (textbox: fabric.Textbox) {
  const width = textbox.width ?? 0;
  const height = textbox.height ?? 0;
  const maxWidth = (textbox.canvas as any)?._textboxCachingLimits?.width;
  const maxHeight = (textbox.canvas as any)?._textboxCachingLimits?.height;
  if (width > maxWidth || height > maxHeight) {
    textbox.objectCaching = false;
  } else {
    // reset object caching to the original value
    textbox.objectCaching = fabric.Textbox.prototype.objectCaching;
  }
};
